import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {NgbActiveModal, NgbModal, NgbModalOptions, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {AddCommentConfig, ESCALATED_TICKETS_EXECUTABLE_ACTIONS_BY_STATUS_ID, TicketComponentPart, TicketComponentWithAttachmentPreview, TicketOperationResult} from '@app/layout/sites/tickets/tickets.interface';
import {EscalatedTicket} from '@shared/services';
import {SiteInfo} from '@shared/services/api/access-sites.interface';
import {TicketArchivedDisplayState, TicketUtils} from '@app/layout/sites/tickets/ticket-utils';
import {defaultTicketModalOptions, TicketDetailsModal, TicketModalUtil} from '@app/layout/sites/tickets/ticket-modals/ticket-modal-util';
import {DeactivateModalOptions, DeactivateUtil} from '@app/shared/util/general/deactivate-utils';
import {CanDeactivateComponent} from '@app/shared/api/candeactivate/can-deactivate';
import {AttachmentPreviewUserAction} from '@app/shared/components/attachments/attachments/attachments.component';
import {TicketActionStateManager} from '../ticket-action-state-manager';


export interface EscalatedTicketDetailsModal extends TicketDetailsModal<EscalatedTicket> {
  addCommentConfig?: AddCommentConfig;
  showAttachmentId?: string;
}


@Component({
  selector: 'app-escalated-ticket-details-modal',
  templateUrl: './escalated-ticket-details-modal.component.html',
  styleUrls: ['./escalated-ticket-details-modal.component.scss'],
  providers: [{
    provide: TicketActionStateManager,
    useFactory: () => new TicketActionStateManager(ESCALATED_TICKETS_EXECUTABLE_ACTIONS_BY_STATUS_ID)
  }],
  standalone: false
})
export class EscalatedTicketDetailsModalComponent implements OnInit, OnDestroy, EscalatedTicketDetailsModal, CanDeactivateComponent, TicketComponentWithAttachmentPreview {

  @Input() ticket: EscalatedTicket;
  @Input() site: SiteInfo;
  // Used to ensure the Add Comment box is shown, useful when e.g. a user clicks a link in an e-mail that should take them to a place
  // where they can add a comment to the ticket, and populate comment text if provided
  @Input() addCommentConfig: AddCommentConfig;
  @Input() showAttachmentId: string;
  @Input() hideInboxControl = false;
  @Input() showSiteName = false;
  @Input() showSiteNameAsLink = true;
  @Input() showViewLinkedTicket = true;
  @Input() showOtherEscalatedTicketsCount = false;
  @Input() showOtherEscalatedTicketsCountAsLink = false;
  @Input() displayArchived: TicketArchivedDisplayState = TicketArchivedDisplayState.NOT_ARCHIVED;
  @Input() readOnly = false;

  @ViewChild('ticketSummaryComponent', { static: true })
  ticketSummaryComponent: TicketComponentPart;

  @ViewChild('ticketBodyComponent', { static: true })
  ticketBodyComponent: TicketComponentPart & CanDeactivateComponent & TicketComponentWithAttachmentPreview;

  // TODO ch: this should actually be a map, ticketOperationResultsByOperation,
  // right now the common scenario is we only have 1 type of operation result
  // however we can actually perform update -> archive
  // then the result would be first UPDATE, then DELETE, need to handle it.
  ticketOperationResult: TicketOperationResult<EscalatedTicket>;

  private readonly ngUnsubscribe: Subject<void> = new Subject();

  // Track if another modal has been opened, if so do not close this modal on ESC key
  private canCloseModal = true;

  @Output() attachmentPreviewUserAction = new EventEmitter<AttachmentPreviewUserAction>();

  static openInModal(modalService: NgbModal, data: EscalatedTicketDetailsModal, ngbOptions?: NgbModalOptions): NgbModalRef {
    const modalRef = modalService.open(EscalatedTicketDetailsModalComponent, {
      ...defaultTicketModalOptions,
      ...ngbOptions,
    });
    const modalInstance: EscalatedTicketDetailsModalComponent = modalRef.componentInstance;
    TicketModalUtil.inflateModalInstance(modalInstance, data);
    modalInstance.showSiteName = data.showSiteName != null ? data.showSiteName : true;
    modalInstance.showSiteNameAsLink = data.showSiteNameAsLink != null ? data.showSiteNameAsLink : true;
    modalInstance.addCommentConfig = data.addCommentConfig || { showAddComment: true };
    modalInstance.showAttachmentId = data.showAttachmentId;
    modalInstance.hideInboxControl = data.hideInboxControl != null ? data.hideInboxControl : false;
    modalInstance.showViewLinkedTicket = data.showViewLinkedTicket != null ? data.showViewLinkedTicket : true;
    modalInstance.showOtherEscalatedTicketsCount = data.showOtherEscalatedTicketsCount != null ? data.showOtherEscalatedTicketsCount : false;
    modalInstance.showOtherEscalatedTicketsCountAsLink = data.showOtherEscalatedTicketsCountAsLink != null ? data.showOtherEscalatedTicketsCountAsLink : false;
    modalInstance.displayArchived = data.displayArchived ?? TicketArchivedDisplayState.NOT_ARCHIVED;
    modalInstance.readOnly = data.readOnly ?? false;

    return modalRef;
  }

  constructor(private activeModal: NgbActiveModal, private modalService: NgbModal) {
  }

  ngOnInit(): void {
    this.modalService.activeInstances.pipe(takeUntil(this.ngUnsubscribe)).subscribe(modals => {
      this.canCloseModal = !(modals?.length > 1)
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // returns boolean to indicate whether ticket details modal would stay open as result of this ticket operation
  private onTicketOperation(result: TicketOperationResult<EscalatedTicket>): boolean {
    if (TicketUtils.closeModal(result, this.displayArchived)) {
      this.activeModal.close(result);
      return false;
    }
    this.ticketOperationResult = TicketUtils.mergeTicketOperationResults(this.ticketOperationResult, result);
    return true;
  }

  onTicketOperationFromSummaryComponent(result: TicketOperationResult<EscalatedTicket>): void {
    const refreshOtherComponents = this.onTicketOperation(result);
    if (refreshOtherComponents) {
      // refresh body component if detail window remains open
      this.ticketBodyComponent.afterTicketUpdate(result);
    }
  }

  onTicketOperationFromBodyComponent(result: TicketOperationResult<EscalatedTicket>): void {
    const refreshOtherComponents = this.onTicketOperation(result);
    if (refreshOtherComponents) {
      // refresh summary component if detail window remains open
      this.ticketSummaryComponent.afterTicketUpdate(result);
    }
  }

  confirmedCloseModal() {
    if (this.ticketOperationResult) {
      this.activeModal.close(this.ticketOperationResult);
    } else {
      this.activeModal.dismiss();
    }
  }

  closeModal() {
    if (this.displayDeactivateConfirmation()) {
      const confirm = DeactivateUtil.confirmDeactivate(
        this.modalService,
        this.getDeactivateModalOptions());
      confirm.subscribe(shouldClose => {
        if (shouldClose) {
          this.confirmedCloseModal();
        }
      });
    } else {
      this.confirmedCloseModal();
    }
  }

  displayDeactivateConfirmation(): boolean {
    return !!this.ticketBodyComponent?.displayDeactivateConfirmation();
  }

  getDeactivateModalOptions(): DeactivateModalOptions {
    return this.ticketBodyComponent.getDeactivateModalOptions();
  }

  @HostListener('window:keyup.escape', ['$event'])
  keyEvent(_event: KeyboardEvent) {
    // Need to return updated ticket even if Escape is pressed on the keyboard. So handle our own keyboard events
    // Track if any nested modals have been opened (Ticket Edit Modal) and only close this modal if there are no nested
    // modals
    if (this.canCloseModal) {
      this.closeModal();
    }
  }

  onAttachmentPreviewUserAction(previewActionEvent: AttachmentPreviewUserAction) {
    this.attachmentPreviewUserAction.emit(previewActionEvent);
  }
}
